import React, { useEffect, useState } from 'react';

import Helmet from 'react-helmet';
import { Box, Grid, Typography } from '@material-ui/core';
import { Trans, useTranslation } from 'react-i18next';

import { ButtonComponent, CardComponent, PageHeader, PageHeaderNav } from 'components';
import { ROUTES } from 'routes/constants';
import { LINKS } from 'components/constants';
import { useAuthState } from 'contexts/AuthContext';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { clientsModel } from 'models';
import { SubscriptionResponse, SubscriptionTier } from 'types';
import Config from 'config/Config';
import { COLORS } from 'theme/constants';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import { useSubmittingState } from 'hooks';

import { BillingPlansDescription, StyledDivider } from './style';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ['stripe-pricing-table']: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

const tierTranslationKeys: Record<SubscriptionTier, string> = {
  [SubscriptionTier.Starter]: 'starter',
  [SubscriptionTier.Professional]: 'professional',
  [SubscriptionTier.Enterprise]: 'enterprise',
};

const BillingSettings = () => {
  const { withPageProgressHandler } = useBusyProgressActions();
  const { handleServerError } = useServerErrorActions();
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();
  const { t, i18n } = useTranslation();

  const dateFormat = new Intl.DateTimeFormat(i18n.language, {
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  function formatDate(dateString: string) {
    return dateFormat.format(new Date(dateString));
  }

  const { currentUser } = useAuthState();

  const [subscription, setSubscription] = useState<SubscriptionResponse | undefined>(undefined);

  useEffect(() => {
    getCurrentSubscription();
  }, [currentUser.attributes.id]);

  const getCurrentSubscription = withPageProgressHandler(() =>
    clientsModel.getCurrentSubscripiton(currentUser.attributes.clientId).then(r => {
      setSubscription(r.data ?? undefined);
    }),
  );

  const expiryPrefix = ((subscription?: SubscriptionResponse) => {
    if (!subscription) return '';
    if (subscription.state === 'active' || subscription.state === 'trialing')
      return t(`pages.settings.billingSettings.expires.${subscription.state}`);
    return t(`pages.settings.billingSettings.expires.inactive`);
  })(subscription);

  const expiryColor = ((subscription?: SubscriptionResponse) => {
    if (!subscription) return '';
    if (subscription.state === 'active') return [COLORS.cozmosGreenLight, COLORS.cozmosGreen];
    if (subscription.state === 'trialing') return [COLORS.cozmosBlueLight, COLORS.cozmosBlue];
    return [COLORS.cozmosRedLight, COLORS.cozmosRed];
  })(subscription);

  const handleButtonClick = withHandlingSubmittingState(() =>
    clientsModel
      .getStripeSubscriptionUrl()
      .then(data => {
        window.open(data.sessionUrl.url);
      })
      .catch(handleServerError),
  );

  return (
    <>
      <PageHeader title={t('pages.settings.billingSettings.title')} backLink={ROUTES.settings}>
        <PageHeaderNav />
      </PageHeader>
      <Grid container>
        <Grid item lg={12}>
          <CardComponent title={t('pages.settings.billingSettings.priceTable.title')} xs={12} sm={12} lg={12}>
            <Grid container spacing={6} direction="column">
              <Grid item>
                <BillingPlansDescription>
                  <Trans
                    i18nKey="pages.settings.billingSettings.priceTable.description"
                    components={{
                      a: <a href={`mailto:${LINKS.contactEmail}`}>link</a>,
                    }}
                    values={{ email: LINKS.contactEmail }}
                  />
                </BillingPlansDescription>
              </Grid>
              <StyledDivider />
              <Grid item>
                {subscription === undefined && (
                  <>
                    <Helmet>
                      <script src="https://js.stripe.com/v3/pricing-table.js" />
                    </Helmet>
                    <stripe-pricing-table
                      pricing-table-id={Config.subscription_table_id}
                      publishable-key={Config.stripe_public_key}
                      client-reference-id={currentUser.attributes.clientId}
                    />
                  </>
                )}
                {subscription !== undefined && (
                  <>
                    <Typography variant="h2">
                      {t('pages.settings.billingSettings.status.tier')}{' '}
                      <Typography variant="inherit" display="inline" color="primary">
                        {t(`pages.settings.billingSettings.status.${tierTranslationKeys[subscription.tier]}`)}
                      </Typography>
                      <Box ml={4} display="inline" bgcolor={expiryColor[0]} padding={2} borderRadius={4}>
                        <Typography variant="inherit" display="inline" style={{ color: expiryColor[1] }}>
                          {expiryPrefix}
                          {formatDate(subscription.periodEnd)}
                        </Typography>
                      </Box>
                    </Typography>
                    <Box mt={4}>
                      <Typography>{t('pages.settings.billingSettings.status.portalHint')}</Typography>
                      <ButtonComponent
                        text={t('pages.settings.billingSettings.status.portalButton')}
                        color="primary"
                        variant="contained"
                        loading={isSubmitting}
                        onClick={handleButtonClick}
                        style={{ minWidth: '182px', minHeight: '36px', fontSize: '14px' }}
                      />
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid item style={{ textAlign: 'center' }}>
              <Typography variant="h2" display="inline">
                {t('pages.settings.billingSettings.paymentsBy')}{' '}
              </Typography>
              <Typography variant="h1" display="inline" color="primary">
                stripe
              </Typography>
            </Grid>
          </CardComponent>
        </Grid>
      </Grid>
    </>
  );
};

export default BillingSettings;
